import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Spinner, BookmarkSimple, EnvelopeSimple } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import VideoScriptCard from "@/components/VideoScriptCard";
import SequenceCard from "@/components/SequenceCard";
import { generateCampaign, generateSequences, createApp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";

const PLATFORMS = ["iOS", "Android", "Web", "iOS + Android"];
const GOALS = ["Downloads", "Subscriptions", "Organic Social Reach"];

const initialForm = {
  app_name: "",
  platform: "",
  core_function: "",
  target_audience: "",
  primary_goal: "",
};

const DemoSection = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sequenceResult, setSequenceResult] = useState(null);
  const [sequenceLoading, setSequenceLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const isValid = form.app_name && form.platform && form.core_function && form.target_audience && form.primary_goal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setError("Fill in every field so the engine can generate a real campaign.");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const data = await generateCampaign(form);
      setResult(data);
      toast.success("Campaign generated");
    } catch (err) {
      setError(err?.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToPortfolio = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setSaving(true);
    try {
      await createApp(form);
      toast.success(`${form.app_name} saved to your portfolio`);
      navigate("/dashboard");
    } catch {
      toast.error("Could not save app");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSequences = async () => {
    setSequenceLoading(true);
    try {
      const data = await generateSequences(form);
      setSequenceResult(data);
      toast.success("Email & push sequences generated");
    } catch {
      toast.error("Sequence generation failed, try again");
    } finally {
      setSequenceLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-24 sm:py-32 px-6 lg:px-12">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-4">Try It Live</p>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mb-16 max-w-2xl">
          Give the engine your app. Get a full campaign back in seconds.
        </h2>

        <form onSubmit={handleSubmit} className="glass-panel rounded-3xl p-6 sm:p-10 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">App Name</Label>
              <Input
                data-testid={HOME.demoAppName}
                value={form.app_name}
                onChange={(e) => update("app_name", e.target.value)}
                placeholder="e.g. FocusFlow"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Platform</Label>
              <Select value={form.platform} onValueChange={(v) => update("platform", v)}>
                <SelectTrigger data-testid={HOME.demoPlatform} className="bg-white/5 border-white/15 text-white h-12">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Core Function</Label>
            <Textarea
              data-testid={HOME.demoCoreFunction}
              value={form.core_function}
              onChange={(e) => update("core_function", e.target.value)}
              placeholder="What does your app actually do? e.g. AI-powered focus timer with habit tracking"
              className="bg-white/5 border-white/15 text-white placeholder:text-white/30 min-h-[90px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Target Audience</Label>
              <Input
                data-testid={HOME.demoTargetAudience}
                value={form.target_audience}
                onChange={(e) => update("target_audience", e.target.value)}
                placeholder="e.g. Gen Z students, remote freelancers"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Primary Goal</Label>
              <Select value={form.primary_goal} onValueChange={(v) => update("primary_goal", v)}>
                <SelectTrigger data-testid={HOME.demoPrimaryGoal} className="bg-white/5 border-white/15 text-white h-12">
                  <SelectValue placeholder="Select goal" />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.p
                data-testid={HOME.demoError}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-sm text-red-400"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <button
            type="submit"
            data-testid={HOME.demoSubmitBtn}
            disabled={loading}
            className="w-full rounded-full bg-[#2563EB] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#3B82F6] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <span data-testid={HOME.demoLoading} className="flex items-center gap-2">
                <Spinner size={18} className="animate-spin" /> Generating your campaign...
              </span>
            ) : (
              "Generate Campaign"
            )}
          </button>
        </form>

        <AnimatePresence>
          {result && (
            <motion.div
              data-testid={HOME.demoResult}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mt-10 space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass-panel rounded-2xl p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-4">Positioning</p>
                  <p className="font-display text-lg font-bold mb-2">{result.positioning?.value_prop}</p>
                  <p className="text-sm text-white/50 mb-3">{result.positioning?.pain_point}</p>
                  <p className="text-sm text-white/40">Segment: {result.positioning?.target_segment}</p>
                </div>
                <div className="glass-panel rounded-2xl p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-4">ASO Metadata</p>
                  <p className="text-sm text-white/70 mb-1"><span className="text-white/40">Title:</span> {result.aso?.title}</p>
                  <p className="text-sm text-white/70 mb-1"><span className="text-white/40">Subtitle:</span> {result.aso?.subtitle}</p>
                  <p className="text-sm text-white/70"><span className="text-white/40">Keywords:</span> {result.aso?.keyword_field}</p>
                </div>
              </div>
              <div className="glass-panel rounded-2xl p-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-3">Growth Loop</p>
                <p className="text-sm text-white/60 leading-relaxed mb-6">{result.growth_loop}</p>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-3">$5-$10/Day Budget Test Plan</p>
                <p className="text-sm text-white/60 leading-relaxed">{result.budget_test_plan}</p>
              </div>
              <VideoScriptCard script={result.video_script} testId="generated-video-script-card" />
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="demo-save-to-portfolio-btn"
                  onClick={handleSaveToPortfolio}
                  disabled={saving}
                  className="rounded-full border border-[#F97316] text-[#F97316] font-semibold px-6 py-3 text-sm hover:bg-[#F97316] hover:text-white transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                >
                  <BookmarkSimple size={16} weight="bold" />
                  {saving ? "Saving..." : user ? "Save This App to My Portfolio" : "Log In to Save This App"}
                </button>
                <button
                  data-testid={HOME.demoGenerateSequencesBtn}
                  onClick={handleGenerateSequences}
                  disabled={sequenceLoading}
                  className="rounded-full border border-[#F97316]/50 text-[#F97316] font-semibold px-6 py-3 text-sm hover:bg-[#F97316] hover:text-white hover:border-[#F97316] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                >
                  {sequenceLoading ? <Spinner size={16} className="animate-spin" /> : <EnvelopeSimple size={16} weight="bold" />}
                  {sequenceLoading ? "Generating..." : "Generate Email & Push Sequences"}
                </button>
              </div>

              {sequenceResult && (
                <motion.div
                  data-testid={HOME.demoSequenceResult}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <SequenceCard sequence={sequenceResult} testId="generated-sequence-card" />
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default DemoSection;
